import {
  Alert,
  AlertTitle,
  CircularProgress,
  FormControl,
  FormLabel,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import { ClockPickerView, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { Product, ShoppingCartItem, Unit, UnavailabilityType } from '@thro-types/leihbar';

import {
  addHours,
  addMinutes,
  format,
  getHours,
  getMinutes,
  isAfter,
  isBefore,
  isSameDay,
  max,
  min,
  setHours,
  setMinutes,
  startOfDay,
  startOfHour,
  sub
} from 'date-fns';
import { Field, useFormikContext } from 'formik';
import { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  useAvailableArticleQuery,
  useLenderAvailabilityQuery,
  useUnavailabilitiesQuery,
  useVisibleArticlesForProductQuery
} from 'api/hooks/leihbar';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import {
  compareWeekdays,
  formatTimeFromMinutes,
  getMaxBookingDuration,
  getEarliestStartTime,
  getMinDate,
  shouldDisableDate,
  shouldDisableTimeByMaxBookingDuration,
  shouldDisableTimeByUnavailabilities
} from 'app/containers/LeihBar/date.helper';
import { translations } from 'locales/i18n';

import { AvailableColorsDescription } from '../../ProductDetailPage/components/AvailableColorsDescription';
import { CustomPickersDay } from '../../ProductDetailPage/components/CustomPickersDay';
import '../styles/ProductModal.scss';
import { AvailabilityDateTimePicker } from './AvailabilityDateTimePicker';

interface Props {
  product: Product;
  handleClose: () => void;
}

export const AvailabilityStep = ({ product, handleClose }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedDateString, setSelectedDateString] = useState<string | undefined>();
  const [selectedHour, setSelectedHour] = useState<number | undefined>();
  const [maxAvailableEndTime, setMaxAvailableEndTime] = useState<Date | undefined>(undefined);
  const { values, touched, errors, handleBlur, setFieldValue } =
    useFormikContext<ShoppingCartItem>();

  const { data: lenderAvailability } = useLenderAvailabilityQuery(product.lenderGroupId);

  const { data: visibleArticles, isLoading } = useVisibleArticlesForProductQuery(product.id);

  const {
    data: { unavailabilities },
    isError
  } = useUnavailabilitiesQuery(product.id);

  if (isError) {
    dispatch(
      AlertActions.error(t(translations.pages.leihbar.product.alerts.error.getAvailabilities()))
    );
    handleClose();
  }

  const { data: availableArticles } = useAvailableArticleQuery(
    !!(values.startTime && values.endTime),
    product.id,
    {
      params: {
        startTime: values.startTime ? new Date(values.startTime).toISOString() : undefined,
        endTime: values.endTime ? new Date(values.endTime).toISOString() : undefined,
        productId: product.id
      }
    }
  );

  const getEarliestStartDateFromEndDate = (day: Date) => {
    return sub(day, getMaxBookingDuration(product));
  };

  const handleStartTimeChange = (value: Date | null, changeType: 'DAY' | 'TIME') => {
    const formattedDay = value ? format(value, 'yyyy-MM-dd') : undefined;
    setSelectedDateString(formattedDay);
    if (changeType === 'DAY' && value) {
      setFieldValue('startTime', getEarliestStartTime(value, product, lenderAvailability));
    } else if (changeType === 'TIME') {
      if (!values.startTime && value) {
        setFieldValue('startTime', getEarliestStartTime(value, product, lenderAvailability));
      } else {
        setFieldValue('startTime', value);
      }
    }
    /**
     * This is to also change the endTime to the correct day without changing the time of it
     */
    if (
      (value && isAfter(value, values.endTime)) ||
      (value && isBefore(value, getEarliestStartDateFromEndDate(values.endTime)))
    ) {
      if (isSameDay(value, values.endTime)) {
        setFieldValue('endTime', value);
        return;
      }
      const endTimeHours = values.endTime.getHours();
      const endTimeMinutes = values.endTime.getMinutes();
      setFieldValue(
        'endTime',
        addMinutes(addHours(startOfDay(value), endTimeHours), endTimeMinutes)
      );
    }
  };

  const handleEndTimeChange = (value: Date | null) => {
    if (!value) return undefined;
    const formattedDay = value ? format(value, 'yyyy-MM-dd') : undefined;
    setSelectedDateString(formattedDay);

    let adjustedEndDate = value;

    if (value && isBefore(value, values.startTime)) {
      adjustedEndDate = addMinutes(values.startTime, 1);
    }

    const earliestStartTime = getEarliestStartTime(value, product, lenderAvailability);

    const dateToUse = max([earliestStartTime, adjustedEndDate]);

    setFieldValue('endTime', dateToUse);
  };

  const getLatestAvailableEndTime = useCallback(() => {
    if (!values.startTime) {
      return undefined;
    }
    const minDays = unavailabilities
      .filter(unav => unav.day >= format(values.startTime, 'yyyy-MM-dd'))
      .map(una => new Date(una.day));
    if (minDays.length === 0) {
      return undefined;
    }
    const minDay = min(minDays);

    const dayUnavailabilities = unavailabilities.filter(
      una =>
        una.day === format(minDay, 'yyyy-MM-dd') &&
        Math.max(...una.value) > new Date(values.startTime).getHours()
    );
    let minHour: number | undefined = undefined;
    for (const unavailability of dayUnavailabilities) {
      if (unavailability.type === UnavailabilityType.DAY) {
        return startOfDay(new Date(unavailability.day));
      }

      if (unavailability.type === UnavailabilityType.HOUR) {
        const minA = Math.min(...unavailability.value);
        if (!minHour || minHour > minA) {
          minHour = minA;
        }
      }
      if (unavailability.type === UnavailabilityType.MINUTE) {
        if (!minHour || minHour > unavailability.hour!) {
          minHour = unavailability.hour;
        }
      }
    }
    const latestAvailability = dayUnavailabilities.find(
      maxb => maxb.hour === minHour || maxb.value.includes(minHour!)
    );
    const date = new Date(`${latestAvailability?.day}`);
    switch (latestAvailability?.type) {
      case UnavailabilityType.HOUR:
        const datetime = startOfHour(addHours(startOfDay(date), minHour!));
        return datetime;
      case UnavailabilityType.MINUTE:
        return addMinutes(
          addHours(startOfDay(date), minHour!),
          Math.min(...latestAvailability.value)
        );
    }
  }, [unavailabilities, values.startTime]);

  const shouldDisableTime = (
    time: number,
    clockType: ClockPickerView,
    startOrEndTime: Date,
    getHours: number,
    startOrEnd: 'START' | 'END'
  ) => {
    const response =
      shouldDisableTimeByMaxBookingDuration(
        time,
        clockType,
        product,
        startOrEndTime,
        getHours,
        startOrEnd,
        lenderAvailability
      ) ||
      shouldDisableTimeByUnavailabilities(
        time,
        clockType,
        selectedDateString,
        unavailabilities,
        selectedHour
      );
    return response;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.preventDefault();
  };

  useEffect(() => {
    setMaxAvailableEndTime(getLatestAvailableEndTime());
  }, [getLatestAvailableEndTime, values.startTime]);

  useEffect(() => {
    setSelectedHour(getHours(values.startTime));
  }, [values.startTime]);

  useEffect(() => {
    setSelectedHour(getHours(values.endTime));
  }, [values.endTime]);

  return (
    <Grid container justifyContent={'center'}>
      <Grid item xs={12} marginBottom={1}>
        <Alert severity="info">
          <AlertTitle>
            {t(translations.pages.leihbar.shoppingCart.modalInfoTitleMaxBooking())}
          </AlertTitle>
          {product.maxPreBooking
            ? t(
                translations.pages.leihbar.shoppingCart.modalInfoTextMaxBookingWithMaxPrebooking(),
                {
                  maxBooking: `${product.maxBooking?.value} ${t(
                    translations.pages.leihbar.product.create.units[product.maxBooking?.unit || 0]()
                  )}`,
                  maxPreBooking: product.maxPreBooking
                }
              )
            : t(translations.pages.leihbar.shoppingCart.modalInfoTextMaxBooking(), {
                maxBooking: `${product.maxBooking?.value} ${t(
                  translations.pages.leihbar.product.create.units[product.maxBooking?.unit || 0]()
                )}`
              })}
        </Alert>
      </Grid>
      {lenderAvailability?.availabilityTimes && (
        <Grid item xs={12} marginBottom={2}>
          <Alert severity="warning">
            <AlertTitle>
              {t(translations.pages.leihbar.shoppingCart.modalInfoTitleAvailabilityTime())}
            </AlertTitle>
            {t(translations.pages.leihbar.shoppingCart.modalInfoTextAvailabilityTime())}
            <br />
            {lenderAvailability?.availabilityTimes.map(pickUpInterval => (
              <div key={pickUpInterval.id}>
                <br />
                {t(translations.pages.leihbar.product.recurringInterval(), {
                  weekday: pickUpInterval.weekdays
                    .slice()
                    .sort(compareWeekdays)
                    .map(wd => t(translations.common.weekdays[wd.toLowerCase()]()))
                    .join(', '),
                  start: formatTimeFromMinutes(pickUpInterval.start),
                  end: formatTimeFromMinutes(pickUpInterval.end)
                })}
              </div>
            ))}
          </Alert>
        </Grid>
      )}
      {isLoading ? (
        <CircularProgress />
      ) : visibleArticles && visibleArticles[1] === 0 ? (
        <Typography variant="h5">
          {t(translations.pages.leihbar.shoppingCart.notAvailable())}
        </Typography>
      ) : product.maxBooking?.unit === Unit.MINUTE || product.maxBooking?.unit === Unit.HOUR ? (
        <Grid container spacing={2}>
          <Grid item>
            <FormControl required fullWidth>
              <FormLabel>{t(translations.pages.leihbar.shoppingCart.bookingDay())}</FormLabel>
              <DatePicker
                value={values.startTime}
                renderInput={props => (
                  <TextField
                    {...props}
                    id="startTime"
                    name="startTime"
                    variant="outlined"
                    error={touched.startTime && Boolean(errors.startTime)}
                    helperText={touched.startTime && String(errors.startTime ?? '')}
                    onBlur={handleBlur}
                    size="small"
                  />
                )}
                shouldDisableDate={day =>
                  shouldDisableDate(
                    day,
                    product,
                    lenderAvailability,
                    unavailabilities,
                    'START',
                    values
                  )
                }
                onChange={value => {
                  handleStartTimeChange(value, 'DAY');
                }}
                inputFormat="dd.MM.yyyy"
                minDate={getMinDate(product)}
                PopperProps={{
                  sx: {
                    '.MuiPickersDay-root': {
                      ':not(.Mui-disabled)': {
                        fontWeight: 450
                      }
                    }
                  }
                }}
                InputProps={{ onKeyDown: e => onKeyDown(e) }}
                renderDay={(day, selectedDays, pickersDayProps) => (
                  <CustomPickersDay
                    day={day}
                    selectedDays={selectedDays}
                    pickersDayProps={pickersDayProps}
                    unavailabilities={unavailabilities}
                    key={day.toString()}
                  />
                )}
              />
            </FormControl>
          </Grid>
          {values.startTime && (
            <>
              <Grid item className="availability-step__appearAnim">
                <FormControl required fullWidth>
                  <FormLabel>{t(translations.pages.leihbar.shoppingCart.pickUpTime())}</FormLabel>
                  <TimePicker
                    value={values.startTime}
                    renderInput={props => (
                      <TextField
                        {...props}
                        id="startTime"
                        name="startTime"
                        variant="outlined"
                        error={touched.startTime && Boolean(errors.startTime)}
                        helperText={touched.startTime && String(errors.startTime ?? '')}
                        onBlur={handleBlur}
                        size="small"
                      />
                    )}
                    shouldDisableTime={(time, clockType) =>
                      shouldDisableTime(
                        time,
                        clockType,
                        values.startTime,
                        getHours(values.startTime),
                        'START'
                      )
                    }
                    onChange={value => {
                      handleStartTimeChange(value, 'TIME');
                    }}
                    ampm={false}
                    ampmInClock={false}
                    minutesStep={15}
                    minTime={new Date()}
                    disableIgnoringDatePartForTimeValidation
                    InputProps={{ onKeyDown: e => onKeyDown(e) }}
                    PopperProps={{
                      sx: {
                        '.MuiClockNumber-root': {
                          '&.Mui-disabled': { opacity: 0.6 },
                          ':not(.Mui-disabled)': {
                            fontWeight: 650
                          }
                        }
                      }
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item className="availability-step__appearAnim">
                <FormControl required fullWidth>
                  <FormLabel>{t(translations.pages.leihbar.shoppingCart.returnTime())}</FormLabel>
                  <TimePicker
                    value={values.endTime}
                    renderInput={props => (
                      <TextField
                        {...props}
                        id="endTime"
                        name="endTime"
                        variant="outlined"
                        error={touched.endTime && Boolean(errors.endTime)}
                        helperText={touched.endTime && String(errors.endTime ?? '')}
                        onBlur={handleBlur}
                        size="small"
                      />
                    )}
                    shouldDisableTime={(time, clockType) =>
                      shouldDisableTime(
                        time,
                        clockType,
                        values.startTime,
                        getHours(values.endTime),
                        'END'
                      )
                    }
                    onChange={value => {
                      value &&
                        setFieldValue(
                          'endTime',
                          setMinutes(setHours(values.startTime, getHours(value)), getMinutes(value))
                        );
                    }}
                    ampm={false}
                    ampmInClock={false}
                    minutesStep={15}
                    minTime={values.startTime || new Date()}
                    InputProps={{ onKeyDown: e => onKeyDown(e) }}
                    PopperProps={{
                      sx: {
                        '.MuiClockNumber-root': {
                          '&.Mui-disabled': { opacity: 0.6 },
                          ':not(.Mui-disabled)': {
                            fontWeight: 650
                          }
                        }
                      }
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs style={{ alignSelf: 'end' }} className="availability-step__appearAnim">
                <Alert severity="info">
                  {t(translations.pages.leihbar.shoppingCart.modalInfoTextNotice())}
                </Alert>
              </Grid>
            </>
          )}
          {product.isAmountPossible && values.startTime && values.endTime && (
            <Grid item xs={12} className="availability-step__appearAnim">
              <FormControl>
                <FormLabel style={{ display: 'flex', gap: 4 }}>
                  {t(translations.pages.leihbar.shoppingCart.amount())}
                </FormLabel>
                <Grid container alignItems={'center'} gap={3}>
                  <Grid>
                    <Field
                      as={TextField}
                      defaultValue={availableArticles.length > 1 ? 1 : 0}
                      value={values.amount}
                      type="number"
                      id="amount"
                      name="amount"
                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: product.maxArticleAmount
                            ? availableArticles.length < product?.maxArticleAmount
                              ? availableArticles.length
                              : product.maxArticleAmount
                            : 1
                        }
                      }}
                      size="small"
                      error={touched.amount && Boolean(errors.amount)}
                      helperText={touched.amount && errors.amount}
                    />
                  </Grid>
                  {product.maxArticleAmount && !!values.startTime && !!values.endTime && (
                    <>
                      {t(translations.pages.leihbar.shoppingCart.availableArticle())}{' '}
                      {availableArticles.length < product.maxArticleAmount
                        ? availableArticles.length
                        : product.maxArticleAmount}
                    </>
                  )}
                </Grid>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel>{t(translations.pages.leihbar.shoppingCart.comment())}</FormLabel>
              <TextField
                id="comment"
                name="comment"
                value={values.comment}
                variant="outlined"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue('comment', e.target.value);
                }}
                placeholder={t(translations.pages.leihbar.shoppingCart.placeholder.comment())}
                size="small"
              />
            </FormControl>
          </Grid>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl required fullWidth>
              <FormLabel>{t(translations.pages.leihbar.shoppingCart.pickUpTime())}</FormLabel>
              <AvailabilityDateTimePicker
                type="START"
                error={touched.startTime && Boolean(errors.startTime)}
                helperText={touched.startTime && String(errors.startTime ?? '')}
                value={values.startTime}
                onChange={value => {
                  handleStartTimeChange(value, 'TIME');
                }}
                onBlur={handleBlur}
                minDateTime={getMinDate(product)}
                shouldDisableDate={day =>
                  shouldDisableDate(
                    day,
                    product,
                    lenderAvailability,
                    unavailabilities,
                    'START',
                    values
                  )
                }
                shouldDisableTime={(time, clockType) =>
                  shouldDisableTime(
                    time,
                    clockType,
                    values.startTime,
                    getHours(values.startTime),
                    'START'
                  )
                }
                unavailabilities={unavailabilities}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl required fullWidth>
              <FormLabel>{t(translations.pages.leihbar.shoppingCart.returnTime())}</FormLabel>
              <AvailabilityDateTimePicker
                type="END"
                error={touched.endTime && Boolean(errors.endTime)}
                helperText={touched.endTime && String(errors.endTime ?? '')}
                value={values.endTime}
                onChange={value => {
                  handleEndTimeChange(value);
                }}
                onBlur={handleBlur}
                onOpen={() => {
                  const formattedDay = values.endTime && format(values.endTime, 'yyyy-MM-dd');
                  setSelectedDateString(formattedDay);
                }}
                minDateTime={values.startTime || new Date()}
                maxDateTime={maxAvailableEndTime} // ? Required to restrict booking over another booking
                shouldDisableDate={day =>
                  shouldDisableDate(
                    day,
                    product,
                    lenderAvailability,
                    unavailabilities,
                    'END',
                    values
                  )
                }
                shouldDisableTime={(time, clockType) =>
                  shouldDisableTime(
                    time,
                    clockType,
                    values.startTime,
                    getHours(values.endTime),
                    'END'
                  )
                }
                unavailabilities={unavailabilities}
              />
            </FormControl>
          </Grid>
          {product.isAmountPossible && values.startTime && values.endTime && (
            <Grid item xs={12} className="availability-step__appearAnim">
              <FormControl>
                <FormLabel style={{ display: 'flex', gap: 4 }}>
                  {t(translations.pages.leihbar.shoppingCart.amount())}
                </FormLabel>
                <Grid container alignItems={'center'} gap={3}>
                  <Grid>
                    <Field
                      as={TextField}
                      value={values.amount}
                      type="number"
                      id="amount"
                      name="amount"
                      InputProps={{
                        inputProps: {
                          min: 1,
                          max: product.maxArticleAmount
                            ? availableArticles.length < product?.maxArticleAmount
                              ? availableArticles.length
                              : product.maxArticleAmount
                            : 1
                        }
                      }}
                      size="small"
                      error={touched.amount && Boolean(errors.amount)}
                      helperText={touched.amount && errors.amount}
                    />
                  </Grid>
                  {product.maxArticleAmount && !!values.startTime && !!values.endTime && (
                    <>
                      {t(translations.pages.leihbar.shoppingCart.availableArticle())}{' '}
                      {availableArticles.length < product.maxArticleAmount
                        ? availableArticles.length
                        : product.maxArticleAmount}
                    </>
                  )}
                </Grid>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel>{t(translations.pages.leihbar.shoppingCart.comment())}</FormLabel>
              <TextField
                id="comment"
                name="comment"
                value={values.comment}
                variant="outlined"
                onBlur={handleBlur}
                onChange={e => {
                  setFieldValue('comment', e.target.value);
                }}
                placeholder={t(translations.pages.leihbar.shoppingCart.placeholder.comment())}
                size="small"
              />
            </FormControl>
          </Grid>
          <AvailableColorsDescription />
        </Grid>
      )}
    </Grid>
  );
};
